"use client";

import { Phone, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TaxQuestions() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const faqs = [{
    question: "What documents do I need for my initial tax consultation?",
    answer: "Please bring your W-2s, 1099s, records of business expenses, mortgage interest statements, and any relevant investment documentation. Our team will provide a comprehensive checklist during your onboarding to ensure we have everything required for accurate compliance and strategic planning."
  }, {
    question: "How does Lovejoy Company handle year-round tax advisory?",
    answer: "We believe tax planning is a continuous process, not a seasonal event. We offer quarterly check-ins to review your financial progress, adjust withholding or estimated payments, and identify new deduction opportunities as your business or personal situation evolves throughout the year."
  }, {
    question: "Can you assist with complex multi-state tax filings?",
    answer: "Yes, our certified tax professionals are well-versed in the regulatory requirements of various jurisdictions. We specialize in navigating the complexities of multi-state income allocation, ensuring you remain compliant while optimizing your tax position across all relevant states."
  }, {
    question: "What is your approach to audit risk management?",
    answer: "We prioritize transparency and rigorous documentation. By maintaining strict ethical standards and ensuring every filing is backed by thorough research and clear records, we proactively minimize audit risk. Should an inquiry arise, we provide full representation and support."
  }, {
    question: "How do I get started with your tax services?",
    answer: "Getting started is simple. You can schedule an initial discovery call through our website. During this session, we will discuss your specific financial goals, review your current tax situation, and outline a personalized roadmap for your financial stability and compliance."
  }];
  return <section data-section-id="986" id="common-tax-questions" className="relative overflow-hidden bg-background pt-[100px] pb-[120px]">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" aria-hidden="true">
        <svg className="absolute top-32 left-[8%] h-20 w-20 text-foreground" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="6" cy="8" r="2" />
          <circle cx="10" cy="5" r="2" />
          <circle cx="14" cy="5" r="2" />
          <circle cx="18" cy="8" r="2" />
          <path d="M12 10c-3 0-5 2.5-5 5 0 2 1.5 3.5 3.5 3.5h3c2 0 3.5-1.5 3.5-3.5 0-2.5-2-5-5-5z" />
        </svg>
        <svg className="absolute top-[40%] right-[6%] h-24 w-24 rotate-12 text-foreground" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="6" cy="8" r="2" />
          <circle cx="10" cy="5" r="2" />
          <circle cx="14" cy="5" r="2" />
          <circle cx="18" cy="8" r="2" />
          <path d="M12 10c-3 0-5 2.5-5 5 0 2 1.5 3.5 3.5 3.5h3c2 0 3.5-1.5 3.5-3.5 0-2.5-2-5-5-5z" />
        </svg>
        <svg className="absolute bottom-32 left-[15%] h-16 w-16 -rotate-12 text-foreground" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="6" cy="8" r="2" />
          <circle cx="10" cy="5" r="2" />
          <circle cx="14" cy="5" r="2" />
          <circle cx="18" cy="8" r="2" />
          <path d="M12 10c-3 0-5 2.5-5 5 0 2 1.5 3.5 3.5 3.5h3c2 0 3.5-1.5 3.5-3.5 0-2.5-2-5-5-5z" />
        </svg>
        <svg className="absolute bottom-[20%] right-[20%] h-20 w-20 rotate-45 text-foreground" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="6" cy="8" r="2" />
          <circle cx="10" cy="5" r="2" />
          <circle cx="14" cy="5" r="2" />
          <circle cx="18" cy="8" r="2" />
          <path d="M12 10c-3 0-5 2.5-5 5 0 2 1.5 3.5 3.5 3.5h3c2 0 3.5-1.5 3.5-3.5 0-2.5-2-5-5-5z" />
        </svg>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {}
          <div className="lg:col-span-[35] lg:col-start-1 lg:col-end-6">
            <div ref={headerRef} className={`lg:sticky lg:top-28 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <span className="inline-block text-xs font-medium tracking-[0.2em] uppercase text-secondary font-default">
                Tax Advisory
              </span>
              <h2 className="font-default font-serif font-medium text-foreground mt-5 text-4xl leading-[1.1] tracking-tight md:text-[44px]">
                Financial *Clarity*
                <br />
                for your future.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground font-default">
                Navigating tax compliance requires precision and foresight. We have compiled the most common inquiries from our clients to help you understand our approach to strategic financial planning and risk management.
              </p>

              {}
              <div className="mt-10 rounded-lg border border-border bg-card p-7">
                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent/10">
                    <Phone className="h-5 w-5 text-accent" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1">
                    <p className="font-default font-serif text-lg font-medium text-foreground leading-snug">
                      Need personal guidance?
                    </p>
                    <p className="mt-1.5 text-sm text-muted-foreground font-default leading-relaxed">
                      Contact our office
                    </p>
                    <Link to="tel:+15550001234" className="mt-2 inline-block font-default font-serif text-xl font-semibold text-foreground hover:text-accent transition-colors duration-200">
                      (555) 000-1234
                    </Link>
                  </div>
                </div>
                <Button asChild className="mt-5 w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-lg h-11 font-default font-semibold">
                  <Link to="#">
                    <Phone className="h-4 w-4" />
                    Schedule a Consultation
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 lg:col-start-6 lg:col-end-13">
            <div ref={listRef} className={`transition-all duration-700 ease-out ${listVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <Accordion type="single" collapsible defaultValue="item-0" className="flex flex-col gap-3">
                {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="group border border-border bg-card px-8 transition-all duration-300 hover:border-accent data-[state=open]:border-accent data-[state=open]:shadow-md">
                    <AccordionTrigger className="py-7 hover:no-underline [&>svg.lucide-chevron-down]:hidden">
                      <span className="font-default font-serif text-lg md:text-[19px] font-medium text-foreground leading-snug pr-4 text-left">
                        {faq.question}
                      </span>
                      <span aria-hidden="true" className="ml-4 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-accent transition-all duration-300 group-data-[state=open]:rotate-45 group-data-[state=open]:border-accent/50 group-data-[state=open]:bg-accent/10">
                        <Plus className="h-4 w-4" strokeWidth={2} />
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-7 pt-0">
                      <div className="border-t border-border pt-5">
                        <p className="text-[15px] leading-[1.75] text-muted-foreground font-default">
                          {faq.answer}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>;
}